import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import apiService, { Product } from '../services/api';
import { toThumbSrc } from '../services/api';
import { useSEO } from '../hooks/useSEO';

const PageWrap = styled.div`
  position: relative;
  background: linear-gradient(180deg, #fdf2f8 0%, #eef2ff 65%, #ffffff 100%);
  padding-bottom: clamp(4.5rem, 8vw, 7rem);
  overflow: hidden;
`;

const SnowTexture = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(circle at 15% 20%, rgba(255, 255, 255, 0.55), transparent 60%),
    radial-gradient(circle at 80% 10%, rgba(255, 255, 255, 0.4), transparent 55%),
    radial-gradient(circle at 40% 85%, rgba(255, 255, 255, 0.3), transparent 60%);
  opacity: 0.85;
`;

const HeroSection = styled.section`
  position: relative;
  margin: clamp(3.5rem, 8vw, 5.5rem) auto 0;
  max-width: 1160px;
  padding: clamp(3.5rem, 8vw, 5.5rem) clamp(2.3rem, 6vw, 4rem);
  border-radius: clamp(2rem, 5vw, 3.4rem);
  background: linear-gradient(135deg, rgba(46, 45, 77, 0.94), rgba(79, 70, 229, 0.75));
  box-shadow: 0 36px 90px rgba(30, 41, 59, 0.34);
  overflow: hidden;
  color: #fff;
`;

const AuroraOrb = styled.div<{ $position: 'tl' | 'br' }>`
  position: absolute;
  width: clamp(240px, 28vw, 340px);
  height: clamp(240px, 28vw, 340px);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(253, 224, 71, 0.55), transparent 62%);
  filter: blur(1px);
  opacity: 0.75;
  ${({ $position }) =>
    $position === 'tl'
      ? 'top: -110px; left: -70px;'
      : 'bottom: -120px; right: -60px;'}
`;

const HeroGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const HeroText = styled.div`
  display: grid;
  gap: clamp(1.6rem, 3vw, 2.6rem);
  position: relative;
  z-index: 1;

  h1 {
    font-size: clamp(3rem, 6vw, 4.4rem);
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: linear-gradient(90deg, #fde68a, #fca5a5, #c4b5fd);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: clamp(1.1rem, 2.4vw, 1.35rem);
    line-height: 1.9;
    color: rgba(255, 255, 255, 0.92);
  }
`;

const HeroBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(0.6rem, 2vw, 1rem);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1.3rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  font-weight: 600;
  font-size: 0.95rem;
`;

const HeroCTASet = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PrimaryCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.95rem 2.1rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #fb7185, #facc15);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 22px 50px rgba(236, 72, 153, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 28px 58px rgba(236, 72, 153, 0.48);
  }
`;

const SecondaryCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 1.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #f8fafc;
  font-weight: 600;
  text-decoration: none;
`;

const HeroPanel = styled.div`
  position: relative;
  padding: clamp(1.9rem, 4vw, 2.8rem);
  border-radius: clamp(1.6rem, 3vw, 2.2rem);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.18), rgba(148, 163, 184, 0.12));
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(26px);
  display: grid;
  gap: 1.1rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  ul {
    margin: 0;
    padding-left: 1.1rem;
    display: grid;
    gap: 0.75rem;
    color: rgba(248, 250, 252, 0.9);
    line-height: 1.6;
  }
`;

const Section = styled.section`
  position: relative;
  margin: clamp(2.8rem, 7vw, 4.6rem) auto 0;
  max-width: 1140px;
  padding: 0 clamp(1.6rem, 4vw, 2.8rem);
`;

const SectionHeading = styled.div`
  text-align: center;
  margin-bottom: clamp(2.2rem, 4vw, 3.2rem);

  h2 {
    font-size: clamp(2.05rem, 4.8vw, 2.9rem);
    color: #312e81;
    font-weight: 700;
  }

  p {
    margin-top: 1rem;
    color: #4b5563;
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.75;
  }
`;

const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(1.6rem, 3vw, 2.4rem);
`;

const HighlightCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 247, 237, 0.95), rgba(240, 249, 255, 0.95));
  border-radius: clamp(1.5rem, 3vw, 2rem);
  padding: clamp(1.8rem, 3vw, 2.4rem);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 26px 60px rgba(148, 163, 184, 0.18);
  display: grid;
  gap: 0.85rem;

  h3 {
    font-size: 1.3rem;
    color: #4338ca;
    font-weight: 600;
  }

  p {
    color: #4c1d95;
    line-height: 1.65;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: clamp(1.5rem, 3vw, 2.4rem);
`;

const ProductCard = styled.div`
  background: #ffffff;
  border-radius: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 30px 70px rgba(148, 163, 184, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ProductVisual = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  background: radial-gradient(circle at top, #fde68a, #f87171, #f43f5e);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1.6rem, 3vw, 2.3rem);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
    border-radius: clamp(1rem, 2vw, 1.4rem);
    box-shadow: 0 16px 36px rgba(124, 58, 237, 0.22);
  }
`;

const ProductBody = styled.div`
  padding: clamp(1.7rem, 3vw, 2.3rem);
  display: grid;
  gap: 1rem;
  color: #334155;

  h4 {
    font-size: 1.15rem;
    color: #312e81;
    font-weight: 600;
  }
`;

const PriceTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.38rem 1rem;
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.12);
  color: #4338ca;
  font-weight: 600;
  font-size: 0.92rem;
`;

const ProductButton = styled(Link)`
  justify-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.6rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ef4444, #f97316);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 16px 32px rgba(239, 68, 68, 0.25);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const TipsGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(1.6rem, 3vw, 2.4rem);

  li {
    background: linear-gradient(135deg, rgba(241, 245, 255, 0.92), rgba(237, 233, 254, 0.92));
    padding: clamp(1.6rem, 3vw, 2.2rem);
    border-radius: clamp(1.4rem, 3vw, 1.9rem);
    border: 1px solid rgba(99, 102, 241, 0.16);
    color: #4338ca;
    line-height: 1.7;
    font-weight: 500;
    box-shadow: 0 24px 55px rgba(99, 102, 241, 0.14);
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(1.5rem, 3vw, 2.4rem);
`;

const TestimonialCard = styled.div`
  background: linear-gradient(140deg, rgba(255, 255, 255, 0.95), rgba(240, 249, 255, 0.95));
  border-radius: clamp(1.4rem, 3vw, 1.9rem);
  padding: clamp(1.7rem, 3vw, 2.3rem);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 24px 55px rgba(148, 163, 184, 0.18);
  color: #4338ca;
  line-height: 1.7;
  font-weight: 500;
`;

const FinalPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(1.5rem, 3vw, 2.4rem);
`;

const InspirationWrap = styled.section`
  position: relative;
  margin: clamp(2.8rem, 7vw, 4.6rem) auto;
  max-width: 1140px;
  padding: clamp(2.4rem, 6vw, 3.6rem) clamp(1.6rem, 5vw, 3.2rem);
  border-radius: clamp(1.8rem, 4vw, 2.8rem);
  background: linear-gradient(135deg, rgba(236, 72, 153, 0.12), rgba(14, 165, 233, 0.12));
  border: 1px solid rgba(79, 70, 229, 0.14);
  box-shadow: 0 34px 80px rgba(15, 23, 42, 0.16);
  overflow: hidden;

  > * {
    position: relative;
    z-index: 1;
  }
`;

const AmbientGlow = styled.div`
  position: absolute;
  inset: -30% -20% auto;
  height: 220%;
  background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.55), transparent 60%),
    radial-gradient(circle at 80% 10%, rgba(79, 70, 229, 0.32), transparent 65%),
    radial-gradient(circle at 40% 90%, rgba(236, 72, 153, 0.24), transparent 62%);
  pointer-events: none;
`;

const InspirationHeader = styled.div`
  position: relative;
  text-align: center;
  max-width: 720px;
  margin: 0 auto clamp(2rem, 4vw, 3rem);
  display: grid;
  gap: 1.1rem;
  color: #1f2937;

  h2 {
    font-size: clamp(2rem, 4.5vw, 2.8rem);
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0 auto;
    line-height: 1.8;
    color: rgba(30, 41, 59, 0.75);
  }
`;

const InspirationCard = styled(Link)`
  position: relative;
  display: grid;
  grid-template-rows: minmax(220px, 1fr) auto;
  border-radius: clamp(1.6rem, 3vw, 2.4rem);
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 26px 70px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 36px 85px rgba(15, 23, 42, 0.18);
  }
`;

const InspirationImage = styled.div`
  position: relative;
  overflow: hidden;
  min-height: clamp(220px, 30vw, 260px);
  background: rgba(30, 41, 59, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(15, 23, 42, 0.28) 100%);
  }

  ${InspirationCard}:hover & img {
    transform: scale(1.06);
  }
`;

const InspirationContent = styled.div`
  position: relative;
  display: grid;
  gap: 0.6rem;
  padding: clamp(1.4rem, 3vw, 2rem);

  h3 {
    font-size: 1.22rem;
    color: #1f2937;
    font-weight: 600;
  }

  p {
    margin: 0;
    line-height: 1.65;
    color: rgba(15, 23, 42, 0.7);
  }
`;

const CardBadge = styled.span`
  width: fit-content;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.12);
  color: #4338ca;
  font-weight: 600;
  font-size: 0.85rem;
`;

const InspirationGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(1.6rem, 3vw, 2.6rem);
`;

const AccentLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.2rem;
  font-weight: 600;
  color: #4338ca;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #1e1b4b;
  }
`;

const SweedsSpotlight = styled.section`
  position: relative;
  max-width: 1140px;
  margin: clamp(2.8rem, 7vw, 4.8rem) auto;
  padding: clamp(2.8rem, 6vw, 3.6rem) clamp(1.8rem, 5vw, 3.4rem);
  border-radius: clamp(1.8rem, 4vw, 2.8rem);
  background: linear-gradient(135deg, #312e81 0%, #4338ca 55%, #7c3aed 100%);
  color: #f9fafb;
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: clamp(2rem, 5vw, 3.6rem);
  overflow: hidden;
  box-shadow: 0 40px 90px rgba(79, 70, 229, 0.45);

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const SpotlightAura = styled.div`
  position: absolute;
  inset: -40% -30% auto;
  height: 200%;
  background: radial-gradient(circle at 20% 25%, rgba(236, 72, 153, 0.45), transparent 62%),
    radial-gradient(circle at 80% 10%, rgba(96, 165, 250, 0.35), transparent 60%);
  opacity: 0.65;
  pointer-events: none;
`;

const SpotlightImageWrap = styled.div`
  position: relative;
  border-radius: clamp(1.6rem, 3vw, 2.4rem);
  overflow: hidden;
  box-shadow: 0 28px 65px rgba(15, 23, 42, 0.32);

  img {
    width: 100%;
    height: clamp(260px, 32vw, 320px);
    object-fit: cover;
    display: block;
  }
`;

const SpotlightLabel = styled.span`
  position: absolute;
  left: 1.4rem;
  bottom: 1.4rem;
  padding: 0.5rem 1.2rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.65);
  color: #f8fafc;
  font-weight: 600;
  font-size: 0.9rem;
`;

const SpotlightContent = styled.div`
  position: relative;
  display: grid;
  gap: 1.1rem;

  h2 {
    font-size: clamp(2.1rem, 4.2vw, 2.9rem);
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.01em;
  }

  p {
    margin: 0;
    line-height: 1.8;
    color: rgba(240, 249, 255, 0.82);
  }
`;

const SpotlightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.75rem;

  li {
    position: relative;
    padding-left: 1.8rem;
    line-height: 1.7;
    color: rgba(248, 250, 252, 0.86);
  }

  li::before {
    content: '•';
    position: absolute;
    left: 0.6rem;
    top: 0.1rem;
    font-size: 1.3rem;
    color: rgba(253, 224, 71, 0.85);
  }
`;

const SpotlightActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.6rem;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SpotlightPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.95rem 2.1rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #fda4af, #fde68a);
  color: #1f2937;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 24px 48px rgba(253, 164, 175, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 28px 56px rgba(253, 164, 175, 0.42);
  }
`;

const SpotlightSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 1.9rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.12);
  color: #f8fafc;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.8);
  }
`;

const formatCurrency = (amount?: number | null) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};

const heroBadges = [
  'Keramiktomtar från Miljögården',
  'LED-granar och glasdekorationer',
  'Julservetter från Sköna Ting',
  'Snabb leverans från Kramfors'
];

const categoryHighlights = [
  {
    title: '🎅 Keramiktomtar från Miljögården',
    description:
      'Tomte med röd sammetsmössa, stickade detaljer och mjuka skägg lyfter hyllor, sideboard och entréer.'
  },
  {
    title: '✨ Glasgranar och LED-kottar',
    description:
      'Grön glasgran, dekorativ kotte med LED och batteridrivna lyktor ger tryggt sken i varje rum.'
  },
  {
    title: '🍽️ Servetter och brickor från Sköna Ting',
    description:
      'Julservetter med röda blommor och vintage-motiv matchar våra brickor för adventsfika och julbord.'
  }
];

const stylingScenarios = [
  {
    title: '✨ Klassisk röd jul',
    description:
      'Ställ Tomte med röd sammetsmössa 25 cm bredvid glasgran och låt en batteridriven lykta ge mjukt sken.'
  },
  {
    title: '❄️ Frostig nordisk känsla',
    description:
      'Kombinera grön keramiktomte med ljuslyktan Skog och våra vita servetter med snöflingor för ett modernt uttryck.'
  },
  {
    title: '🎁 Adventsfika i fönstret',
    description:
      'Placera dekorativ kotte med LED, en bricka från Sköna Ting och Sweeds glögggodis för att bjuda in till mys.'
  }
];

const moodTips = [
  'Placera tomtarna i grupper: en hög tomte, en sittande variant och en glasgran ger dynamik.',
  'Använd våra julservetter med röda blommor som enkel löpare på bordet och toppa med en LED-kotte.',
  'Blanda doftljus med smak av glögg och cottonflower för att få både tradition och friskhet.',
  'Ställ Sweeds glögg-godis tillsammans med Majas Cottage-koppar på en bricka för spontan glöggfika.'
];

const testimonials = [
  {
    quote: 'Det känns som att kliva in i en saga – tomtarna och juldekorationerna gör hela huset varmt.',
    author: '– Malin, Kramfors'
  },
  {
    quote: 'Porslinstomtarna och doftljusen är så välgjorda! Julen doftar pepparkaka redan i november.',
    author: '– Johan & Sara'
  }
];

const fallbackProducts = [
  {
    title: 'Tomte med röd sammetsmössa 25 cm',
    description: 'En klassisk tomte med mjukt skägg och sammetsmössa som gör varje hylla julredo.',
    image: 'https://lillaflora.nu/api/image/opt_68f3507cbedae_1760776316.jpg',
    link: '/produkter/tomte-med-rod-sammetsmossa-25-cm'
  },
  {
    title: 'Dekorativ kotte med LED-belysning',
    description: 'Skimrande kotte i glas med inbyggd LED som sprider ett mjukt, tryggt ljus.',
    image: 'https://lillaflora.nu/api/image/opt_68f350cc6c4b4_1760776396.jpg',
    link: '/produkter/dekorativ-kotte-med-led-belysning-17x20-cm-alot'
  },
  {
    title: 'Julservetter med röda blommor',
    description: 'Servetter från Sköna Ting som gör dukningen festlig även till vardags.',
    image: 'https://lillaflora.nu/api/image/opt_68f4d9f609646_1760877046.jpg',
    link: '/produkter/julservetter-med-roda-blommor-skona-ting'
  }
];

const inspirationCards = [
  {
    title: 'Tomte med röd sammetsmössa',
    description: 'Handmålad favorit från Miljögården som står stadigt på spiselkransen eller i hallen.',
    image: 'https://lillaflora.nu/api/image/opt_68f3507cbedae_1760776316.jpg',
    link: '/produkter/tomte-med-rod-sammetsmossa-25-cm',
    badge: 'Tomtar'
  },
  {
    title: 'Glittrande glasgran',
    description: 'Placera den gröna glasgranen på en bricka och addera en ljusslinga för ett magiskt sken.',
    image: 'https://lillaflora.nu/api/image/opt_68f353c218410_1760777154.jpg',
    link: '/produkter/gron-glasgran-med-glittrig-yta',
    badge: 'Belysning'
  },
  {
    title: 'LED-kotte i glas',
    description: 'Den dekorativa kotten med varmvitt LED-ljus gör sig lika bra i fönstret som på bordet.',
    image: 'https://lillaflora.nu/api/image/opt_68f350cc6c4b4_1760776396.jpg',
    link: '/produkter/dekorativ-kotte-med-led-belysning-17x20-cm-alot',
    badge: 'Detaljer'
  },
  {
    title: 'Julservetter med röda blommor',
    description: 'Servetterna från Sköna Ting ger adventsfikat den där extra festliga känslan.',
    image: 'https://lillaflora.nu/api/image/opt_68f4d9f609646_1760877046.jpg',
    link: '/produkter/julservetter-med-roda-blommor-skona-ting',
    badge: 'Dukning'
  }
];

const sweedsHighlight = {
  image: 'https://lillaflora.nu/api/image/opt_68bed47403aee_1757336692.jpg',
  label: 'SWEEDS · Glögg',
  heading: 'Julens smakfavorit från SWEEDS',
  description:
    'Christmas SWEEDS med Saturnus ekologiska vinglögg är veganskt, glutenfritt och fullproppat med julens kryddor. Perfekt komplement till dina tomtar och julservetter.',
  bullets: [
    'Servera tillsammans med Majas Cottage-koppar och en ljuslykta för en komplett fikastund.',
    'Alkoholfri smakupplevelse som hela familjen kan njuta av.',
    'Levereras i dekorativ burk som blir en del av julpyntet på hyllan.'
  ],
  primaryLink: '/produkter/lxyigt-godis-med-smak-av-glogg-fran-sweeds',
  secondaryLink: '/produkter?kategori=lyxigt-godis-och-annat'
};

const Julpynt: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    entityType: 'page',
    entityId: 'julpynt',
    fallbackTitle: 'Julpynt & Tomtar – Skapa årets mysigaste jul | Lillaflora',
    fallbackDescription:
      'Upptäck tomtar från Miljögården, LED-granar, dekorativa kottar och julservetter från Sköna Ting. Inspirerande tips och julprodukter från Lillaflora.'
  });

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await apiService.getProducts({ category: 'julpynt', limit: 60 });
        setProducts((response || []).slice(0, 8));
      } catch (error) {
        console.error('Failed to load julpynt products', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const productBlocks = useMemo(() => {
    if (products.length === 0) {
      return fallbackProducts.map((item, index) => (
        <ProductCard key={`fallback-${index}`}>
          <ProductVisual>
            {item.image ? (
              <img src={item.image} alt={item.title} loading="lazy" />
            ) : (
              <span role="img" aria-hidden>🎄</span>
            )}
          </ProductVisual>
          <ProductBody>
            <h4>{item.title}</h4>
            <p>{item.description}</p>
            <ProductButton to={item.link}>Utforska sortimentet →</ProductButton>
          </ProductBody>
        </ProductCard>
      ));
    }

    return products.map(product => {
      const image = product.images && product.images.length > 0 ? toThumbSrc(product.images[0]) : null;
      const excerpt = product.short_description || product.description || '';
      const price = product.final_price ?? product.price ?? 0;

      return (
        <ProductCard key={product.slug}>
          <ProductVisual>
            {image ? <img src={image} alt={product.name} loading="lazy" /> : <span role="img" aria-hidden>🕯️</span>}
          </ProductVisual>
          <ProductBody>
            <h4>{product.name}</h4>
            <p>{excerpt.length > 120 ? `${excerpt.slice(0, 120)}…` : excerpt}</p>
            <PriceTag>✨ {formatCurrency(price)}</PriceTag>
            <ProductButton to={`/produkter/${product.slug}`}>Se produkten →</ProductButton>
          </ProductBody>
        </ProductCard>
      );
    });
  }, [products]);

  return (
    <PageWrap>
      <SnowTexture />
      <HeroSection>
        <AuroraOrb $position="tl" />
        <AuroraOrb $position="br" />
        <HeroGrid>
          <HeroText>
            <div style={{ fontSize: '2.4rem' }}>✨</div>
            <h1>Julpynt & tomtar för ett sagolikt hem</h1>
            <p>
              Skapa ett hem som doftar pepparkaka och gnistrar av ljus. Hos Lillaflora hittar du handplockade porslinstomtar,
              ljuvliga juldekorationer och detaljer som binder ihop ett varmt vinterhem.
            </p>
            <HeroBadges>
              {heroBadges.map(tag => (
                <Badge key={tag}>🌟 {tag}</Badge>
              ))}
            </HeroBadges>
            <HeroCTASet>
              <PrimaryCTA to="/produkter?kategori=julpynt">
                Handla julens favoriter <span aria-hidden>→</span>
              </PrimaryCTA>
              <SecondaryCTA to="/produkter?kategori=ljuslyktor-och-dekoration">
                Upptäck ljuslyktor och LED
              </SecondaryCTA>
            </HeroCTASet>
          </HeroText>
          <HeroPanel>
            <h3>Så bygger du magin:</h3>
            <ul>
              <li>Välj en huvudfigur - exempelvis Tomte med röd sammetsmössa eller tomten med rutiga ben från Miljögården.</li>
              <li>Kombinera med grön glasgran eller dekorativ kotte med LED för att skapa höjd och ljusspel.</li>
              <li>Addera julservetter med röda blommor och Sweeds glögg-godis på en bricka för extra färg och smak.</li>
            </ul>
          </HeroPanel>
        </HeroGrid>
      </HeroSection>

      <Section>
        <SectionHeading>
          <h2>Mysiga fokusområden att börja med</h2>
          <p>
            Kombinera porslinstomtar, belysning och textilier för ett julhem som känns genomtänkt men varmt. Här är tre enkla
            startpunkter.
          </p>
        </SectionHeading>
        <HighlightGrid>
          {categoryHighlights.map(item => (
            <HighlightCard key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </HighlightCard>
          ))}
        </HighlightGrid>
      </Section>

      <Section>
        <SectionHeading>
          <h2>Handplockade favoriter</h2>
          <p>
            {loading
              ? 'Vi samlar våra julprodukter åt dig – håll ut!'
              : 'Direktlänkar till våra mest älskade tomtar, ljus och glänsande detaljer.'}
          </p>
        </SectionHeading>
        <ProductGrid>{productBlocks}</ProductGrid>
      </Section>

      <InspirationWrap>
        <AmbientGlow />
        <InspirationHeader>
          <h2>Bildinspiration från butiken</h2>
          <p>
            Våra stylister har kombinerat tomtar, LED-ljus och dukningsdetaljer för att ge dig en snabb överblick av hur
            produkterna spelar tillsammans hemma.
          </p>
        </InspirationHeader>
        <InspirationGrid>
          {inspirationCards.map(card => (
            <InspirationCard to={card.link} key={card.title}>
              <InspirationImage>
                <img src={card.image} alt={card.title} loading="lazy" />
              </InspirationImage>
              <InspirationContent>
                {card.badge && <CardBadge>{card.badge}</CardBadge>}
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                <AccentLink to={card.link}>Se produkten →</AccentLink>
              </InspirationContent>
            </InspirationCard>
          ))}
        </InspirationGrid>
      </InspirationWrap>

      <Section>
        <SectionHeading>
          <h2>Tre stilar – oändligt med mys</h2>
          <p>
            Välj ett tema och kombinera detaljer. Mixen av tradition och trend gör att ditt hem känns både nytt och välbekant.
          </p>
        </SectionHeading>
        <HighlightGrid>
          {stylingScenarios.map(item => (
            <HighlightCard key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </HighlightCard>
          ))}
        </HighlightGrid>
      </Section>

      <SweedsSpotlight>
        <SpotlightAura />
        <SpotlightImageWrap>
          <img src={sweedsHighlight.image} alt="SWEEDS Christmas med glöggsmak" loading="lazy" />
          <SpotlightLabel>{sweedsHighlight.label}</SpotlightLabel>
        </SpotlightImageWrap>
        <SpotlightContent>
          <h2>{sweedsHighlight.heading}</h2>
          <p>{sweedsHighlight.description}</p>
          <SpotlightList>
            {sweedsHighlight.bullets.map(point => (
              <li key={point}>{point}</li>
            ))}
          </SpotlightList>
          <SpotlightActions>
            <SpotlightPrimary to={sweedsHighlight.primaryLink}>Smaka glögggodiset →</SpotlightPrimary>
            <SpotlightSecondary to={sweedsHighlight.secondaryLink}>Alla SWEEDS-smaker</SpotlightSecondary>
          </SpotlightActions>
        </SpotlightContent>
      </SweedsSpotlight>

      <Section>
        <SectionHeading>
          <h2>Inredningsknep för julstämningen</h2>
          <p>
            Små justeringar gör stor skillnad: jobba i lager, använd höjdskillnader och låt doft och ljus guida gästerna genom
            hemmet.
          </p>
        </SectionHeading>
        <TipsGrid>
          {moodTips.map(tip => (
            <li key={tip}>{tip}</li>
          ))}
        </TipsGrid>
      </Section>

      <Section>
        <SectionHeading>
          <h2>Våra kunder berättar</h2>
          <p>
            Julälskare som valt Lillaflora för att skapa varma vinterhem – låt deras ord inspirera ditt pyntande.
          </p>
        </SectionHeading>
        <TestimonialsGrid>
          {testimonials.map(item => (
            <TestimonialCard key={item.quote}>
              “{item.quote}”
              <br />
              <strong style={{ display: 'block', marginTop: '0.6rem' }}>{item.author}</strong>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </Section>

      <Section>
        <FinalPanel>
          <TestimonialCard>
            <h3>Planera ditt dröm-julhem</h3>
            <p>
              Låt tomtar, glasgranar och glimmande ljus bilda små scener. Hos Lillaflora hittar du mixen av tradition och
              nutid som gör julen alldeles magisk.
            </p>
            <p>
              <strong>“{testimonials[0].quote}”</strong>
              <br />
              <span>{testimonials[0].author}</span>
            </p>
          </TestimonialCard>
          <HighlightCard>
            <h3>Redo att börja pyntet?</h3>
            <p>
              Mixa handplockade produkter från vårt julsortiment eller filtrera bland kategorierna för att hitta dina egna
              stjärnor.
            </p>
            <PrimaryCTA to="/produkter?search=jul">Utforska alla julprodukter →</PrimaryCTA>
          </HighlightCard>
        </FinalPanel>
      </Section>
    </PageWrap>
  );
};

export default Julpynt;
